import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CostCalculatorPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Calculator state
  const [roleTitle, setRoleTitle] = useState('');
  const [usSalary, setUsSalary] = useState('');
  const [salaryType, setSalaryType] = useState<'annual' | 'monthly'>('annual');
  const [benefitsPercent, setBenefitsPercent] = useState('25');
  const [numberOfHires, setNumberOfHires] = useState('1');
  const [esSalary, setEsSalary] = useState('');
  
  // Results state
  const [hasCalculated, setHasCalculated] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [results, setResults] = useState({
    usTotalCost: 0,
    esTotalCost: 0,
    savings: 0,
    percentReduction: 0
  });
  
  // Lead form state
  const [leadFormData, setLeadFormData] = useState({
    fullName: '',
    email: '',
    company: '',
    roleHiringFor: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      if (isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };
    
    if (isMobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isMobileMenuOpen]);

  const getDefaultEsSalary = (role: string): number => {
    const roleLower = role.toLowerCase();
    if (roleLower.includes('sales') || roleLower.includes('sdr')) return 1200;
    if (roleLower.includes('customer support') || roleLower.includes('support')) return 900;
    if (roleLower.includes('marketing')) return 1200;
    return 1000;
  };

  const calculateResults = () => {
    if (!usSalary || parseFloat(usSalary) <= 0) return;

    const usAnnualSalary = salaryType === 'monthly' 
      ? parseFloat(usSalary) * 12 
      : parseFloat(usSalary);
    
    const benefits = parseFloat(benefitsPercent) / 100;
    const hires = parseInt(numberOfHires) || 1;
    
    const esMonthly = esSalary && parseFloat(esSalary) > 0
      ? parseFloat(esSalary)
      : getDefaultEsSalary(roleTitle);
    
    const usTotalCost = usAnnualSalary * hires * (1 + benefits);
    const esTotalCost = esMonthly * 12 * hires;
    const savings = usTotalCost - esTotalCost;
    const percentReduction = (savings / usTotalCost) * 100;

    setResults({
      usTotalCost,
      esTotalCost,
      savings,
      percentReduction
    });
    
    setHasCalculated(true);
  };

  const handleReset = () => {
    setRoleTitle('');
    setUsSalary('');
    setSalaryType('annual');
    setBenefitsPercent('25');
    setNumberOfHires('1');
    setEsSalary('');
    setHasCalculated(false);
    setIsUnlocked(false);
    setLeadFormData({
      fullName: '',
      email: '',
      company: '',
      roleHiringFor: ''
    });
  };

  const handleLeadFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!leadFormData.email) return;
    
    setIsSubmitting(true);

    try {
      const response = await fetch('https://formspree.io/f/xaqbvybl', {
        method: 'POST',
        body: JSON.stringify({
          fullName: leadFormData.fullName,
          email: leadFormData.email,
          company: leadFormData.company || '',
          roleHiringFor: leadFormData.roleHiringFor || '',
          calculatorResults: `US Cost: $${results.usTotalCost.toLocaleString()} | ES Cost: $${results.esTotalCost.toLocaleString()} | Savings: $${results.savings.toLocaleString()}`,
          _subject: 'New Cost Calculator Lead - Cader Talent',
          _replyto: leadFormData.email
        }),
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        setIsUnlocked(true);
        
        setTimeout(() => {
          window.location.href = '/thank-you';
        }, 2000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMobileMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 py-0 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="https://static.readdy.ai/image/5287d4f4544f3b52e73e5c6adbaab8fc/8a8de03178bcb555df0aaf16906b9e92.png" alt="Cader Talent" className="h-40 w-auto" />
          </Link>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/#how-it-works" className="text-base font-medium text-[#1A1A1A] transition-colors hover:text-[#C67B5C]">How It Works</Link>
            <Link to="/#why-us" className="text-base font-medium text-[#1A1A1A] transition-colors hover:text-[#C67B5C]">Why Us</Link>
            <Link to="/#pricing" className="text-base font-medium text-[#1A1A1A] transition-colors hover:text-[#C67B5C]">Pricing</Link>
            <Link to="/cost-calculator" className="text-base font-medium text-[#C67B5C] transition-colors hover:text-[#C67B5C]">Cost Calculator</Link>
            <Link to="/#about" className="text-base font-medium text-[#1A1A1A] transition-colors hover:text-[#C67B5C]">About</Link>
            <a href="tel:+18329243888" className="flex items-center gap-2 text-base font-medium text-[#1A1A1A] transition-colors hover:text-[#C67B5C]">
              <i className="ri-phone-line"></i>
              (832) 924-3888
            </a>
            <Link to="/#contact" className="bg-[#C67B5C] text-white px-6 py-2.5 rounded-full font-medium hover:bg-[#B36A4D] transition-colors whitespace-nowrap cursor-pointer">Book a Conversation</Link>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              setIsMobileMenuOpen(!isMobileMenuOpen);
            }}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg text-[#1A1A1A] hover:bg-gray-100 transition-colors"
            aria-label="Toggle menu"
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div 
            className="lg:hidden bg-white shadow-lg border-t border-gray-100"
            onClick={handleMobileMenuClick}
          >
            <div className="px-6 py-4 space-y-4">
              <Link to="/#how-it-works" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">How It Works</Link>
              <Link to="/#why-us" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Why Us</Link>
              <Link to="/#pricing" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Pricing</Link>
              <Link to="/cost-calculator" onClick={closeMobileMenu} className="block text-base font-medium text-[#C67B5C] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">Cost Calculator</Link>
              <Link to="/#about" onClick={closeMobileMenu} className="block text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">About</Link>
              <a href="tel:+18329243888" onClick={closeMobileMenu} className="flex items-center gap-2 text-base font-medium text-[#1A1A1A] hover:text-[#C67B5C] transition-colors py-2 cursor-pointer">
                <i className="ri-phone-line"></i>
                (832) 924-3888
              </a>
              <Link to="/#contact" onClick={closeMobileMenu} className="block bg-[#C67B5C] text-white px-6 py-3 rounded-full font-medium hover:bg-[#B36A4D] transition-colors text-center whitespace-nowrap cursor-pointer">Book a Conversation</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-40 pb-16 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-8 text-center">
          {/* Value Proposition Badge */}
          <div className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-5 py-2.5 rounded-full text-sm font-bold mb-6 shadow-lg">
            <i className="ri-shield-check-line text-lg"></i>
            <span>NO DEPOSIT – Pay only if you hire</span>
          </div>
          <h1 className="text-5xl font-bold text-[#1A1A1A] mb-4">Hiring Cost Calculator</h1>
          <p className="text-xl text-[#1A1A1A]/80 mb-3">Compare the cost of hiring in the U.S. vs hiring vetted remote talent from El Salvador.</p>
          <p className="text-base text-[#1A1A1A]/60">Get an instant estimate of potential savings.</p>
        </div>
      </section>

      {/* NO DEPOSIT Value Proposition Section */}
      <section className="py-16 bg-gradient-to-r from-[#C67B5C] to-[#B36A4D] relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3"></div>
        </div>
        
        <div className="max-w-5xl mx-auto px-8 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div className="text-center lg:text-left flex-1">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-4">
                <i className="ri-shield-check-line text-xl text-white"></i>
                <span className="text-white text-sm font-bold uppercase tracking-wider">Our Commitment</span>
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-white mb-4">
                NO DEPOSIT
              </h2>
              <p className="text-xl text-white/90 mb-6 leading-relaxed">
                We're so confident in our candidates that we don't ask for payment upfront. You only pay when you hire.
              </p>
              
              {/* How It Works */}
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-6 py-5 mb-4 text-left">
                <h3 className="text-white font-bold text-lg mb-3">How it works:</h3>
                <ul className="space-y-2 text-white/95">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>We vet and present 2–3 qualified candidates</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>You interview and choose who fits best</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-xl flex-shrink-0 mt-0.5"></i>
                    <span>Pay $1,000 only when you make the hire</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 inline-block">
                <p className="text-white text-base">
                  <strong className="font-bold">Zero risk. Zero upfront cost.</strong><br />
                  This is how confident we are in our talent.
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <a 
                href="tel:+18329243888" 
                className="inline-flex items-center gap-3 bg-white text-[#C67B5C] px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/90 transition-all shadow-lg cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Call (832) 924-3888
              </a>
              <a 
                href="mailto:mauricio@cadertalent.com" 
                className="inline-flex items-center gap-3 bg-transparent text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white/10 transition-all border-2 border-white cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Calculator Card */}
      <section className="pb-16">
        <div className="max-w-2xl mx-auto px-8">
          <div className="relative bg-white rounded-3xl shadow-lg p-8 md:p-10">
            {/* Locked Overlay - appears after calculation */}
            {hasCalculated && !isUnlocked && (
              <div className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-3xl z-10 flex items-center justify-center p-8">
                <div className="max-w-md w-full">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C67B5C]/10 mb-4">
                      <i className="ri-lock-line text-3xl text-[#C67B5C]"></i>
                    </div>
                    <h3 className="text-2xl font-bold text-[#1A1A1A] mb-2">Unlock your full savings breakdown</h3>
                    <p className="text-base text-[#1A1A1A]/60">Enter your email and we'll send the full calculation + a sample compensation benchmark.</p>
                  </div>

                  <form id="unlock-form" onSubmit={handleLeadFormSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="fullName"
                        value={leadFormData.fullName}
                        onChange={(e) => setLeadFormData({...leadFormData, fullName: e.target.value})}
                        placeholder="Full Name"
                        required
                        className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        value={leadFormData.email}
                        onChange={(e) => setLeadFormData({...leadFormData, email: e.target.value})}
                        placeholder="Work Email"
                        required
                        className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="company"
                        value={leadFormData.company}
                        onChange={(e) => setLeadFormData({...leadFormData, company: e.target.value})}
                        placeholder="Company (optional)"
                        className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        name="roleHiringFor"
                        value={leadFormData.roleHiringFor}
                        onChange={(e) => setLeadFormData({...leadFormData, roleHiringFor: e.target.value})}
                        placeholder="Role hiring for (optional)"
                        className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                      />
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#C67B5C] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#B36A4D] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                    >
                      {isSubmitting ? 'Unlocking...' : 'Unlock Results'}
                    </button>
                  </form>
                </div>
              </div>
            )}

            {/* Results Overlay - appears after unlocking */}
            {hasCalculated && isUnlocked && (
              <div className="absolute inset-0 bg-white rounded-3xl z-10 flex flex-col p-8 md:p-10 overflow-y-auto">
                <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6 text-center">Your Savings Breakdown</h3>
                
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center p-4 bg-[#FAFAF8] rounded-xl">
                    <span className="text-base font-medium text-[#1A1A1A]">US Total Cost</span>
                    <span className="text-xl font-bold text-[#1A1A1A]">${results.usTotalCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-[#FAFAF8] rounded-xl">
                    <span className="text-base font-medium text-[#1A1A1A]">El Salvador Total Cost</span>
                    <span className="text-xl font-bold text-[#C67B5C]">${results.esTotalCost.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-[#C67B5C]/10 rounded-xl border-2 border-[#C67B5C]">
                    <span className="text-base font-bold text-[#1A1A1A]">Estimated Annual Savings</span>
                    <span className="text-2xl font-bold text-[#C67B5C]">${results.savings.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex justify-between items-center p-4 bg-[#FAFAF8] rounded-xl">
                    <span className="text-base font-medium text-[#1A1A1A]">Estimated % Reduction</span>
                    <span className="text-xl font-bold text-[#C67B5C]">{results.percentReduction.toFixed(1)}%</span>
                  </div>
                </div>

                <div className="bg-[#C67B5C]/5 border-l-4 border-[#C67B5C] p-4 rounded-r-xl mb-6">
                  <p className="text-base text-[#1A1A1A]/80">
                    <strong className="text-[#C67B5C]">Many companies save 50–70%</strong> depending on the role.
                  </p>
                </div>

                <div className="bg-[#FAFAF8] rounded-xl p-4 mb-6">
                  <h4 className="text-sm font-semibold text-[#1A1A1A] mb-2 uppercase tracking-wider">Assumptions &amp; Disclaimer</h4>
                  <p className="text-sm text-[#1A1A1A]/60 leading-relaxed">
                    These are estimates for planning purposes. Final costs vary by role, seniority, benefits, and hiring structure.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleReset}
                  className="w-full px-8 py-4 border-2 border-[#1A1A1A]/20 text-[#1A1A1A] rounded-full text-base font-medium hover:border-[#C67B5C] hover:text-[#C67B5C] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Calculate Again
                </button>
              </div>
            )}

            <div className="space-y-6">
              {/* Role Title */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Role Title <span className="text-[#1A1A1A]/40 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={roleTitle}
                  onChange={(e) => setRoleTitle(e.target.value)}
                  placeholder="e.g., Sales Development Representative"
                  className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                />
              </div>

              {/* US Salary */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  US Salary
                </label>
                <div className="flex gap-3">
                  <input
                    type="number"
                    value={usSalary}
                    onChange={(e) => setUsSalary(e.target.value)}
                    placeholder="60000"
                    className="flex-1 px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                  />
                  <div className="flex bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setSalaryType('annual')}
                      className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        salaryType === 'annual' 
                          ? 'bg-[#C67B5C] text-white' 
                          : 'text-[#1A1A1A]/60 hover:text-[#1A1A1A]'
                      }`}
                    >
                      Annual
                    </button>
                    <button
                      type="button"
                      onClick={() => setSalaryType('monthly')}
                      className={`px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                        salaryType === 'monthly' 
                          ? 'bg-[#C67B5C] text-white' 
                          : 'text-[#1A1A1A]/60 hover:text-[#1A1A1A]'
                      }`}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
              </div>

              {/* Benefits & Taxes */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Benefits &amp; Taxes %
                </label>
                <input
                  type="number"
                  value={benefitsPercent}
                  onChange={(e) => setBenefitsPercent(e.target.value)}
                  placeholder="25"
                  className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                />
              </div>

              {/* Number of Hires */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  Number of Hires
                </label>
                <input
                  type="number"
                  value={numberOfHires}
                  onChange={(e) => setNumberOfHires(e.target.value)}
                  placeholder="1"
                  min="1"
                  className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                />
              </div>

              {/* El Salvador Salary */}
              <div>
                <label className="block text-sm font-semibold text-[#1A1A1A] mb-2">
                  El Salvador Salary (monthly) <span className="text-[#1A1A1A]/40 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  value={esSalary}
                  onChange={(e) => setEsSalary(e.target.value)}
                  placeholder="1000"
                  className="w-full px-4 py-3 bg-[#FAFAF8] border border-[#1A1A1A]/10 rounded-xl text-base text-[#1A1A1A] placeholder:text-[#1A1A1A]/40 focus:outline-none focus:border-[#C67B5C] transition-colors"
                />
                <p className="text-sm text-[#1A1A1A]/50 mt-2">If blank, we'll use a conservative benchmark.</p>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={calculateResults}
                  className="flex-1 bg-[#C67B5C] text-white px-8 py-4 rounded-full text-base font-medium hover:bg-[#B36A4D] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Calculate Savings
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="px-8 py-4 border-2 border-[#1A1A1A]/20 text-[#1A1A1A] rounded-full text-base font-medium hover:border-[#C67B5C] hover:text-[#C67B5C] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-32 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-[#C67B5C] text-sm font-semibold uppercase tracking-widest mb-4">Pricing</p>
            <h2 className="text-5xl font-bold text-[#1A1A1A] mb-6">Simple, transparent pricing</h2>
            <p className="text-lg text-[#1A1A1A]/70 max-w-2xl mx-auto">No hidden fees. No long-term contracts. Just straightforward pricing that aligns our success with yours.</p>
          </div>
          
          <div className="max-w-xl mx-auto">
            {/* Value Proposition Banner */}
            <div className="bg-gradient-to-r from-[#C67B5C] to-[#B36A4D] rounded-2xl p-6 mb-6 text-center shadow-lg relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                  <p className="text-white text-sm font-bold uppercase tracking-wider">Our Commitment</p>
                </div>
                <p className="text-white text-3xl font-bold mb-2">NO DEPOSIT</p>
                <p className="text-white/90 text-base mb-4">Pay only when you hire. Zero risk.</p>
                
                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-4">
                  <a 
                    href="tel:+18329243888" 
                    className="inline-flex items-center gap-2 bg-white text-[#C67B5C] px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    Call (832) 924-3888
                  </a>
                  <a 
                    href="mailto:mauricio@cadertalent.com" 
                    className="inline-flex items-center gap-2 bg-white/20 text-white px-6 py-2.5 rounded-full text-sm font-semibold hover:bg-white/30 transition-colors border border-white/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-line"></i>
                    Email Us
                  </a>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-10 shadow-lg border-2 border-[#C67B5C]">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-[#C67B5C]/10 mb-6">
                  <i className="ri-user-star-line text-3xl text-[#C67B5C]"></i>
                </div>
                <h3 className="text-3xl font-bold text-[#1A1A1A] mb-2">Individual Plan</h3>
                <div className="mt-6">
                  <div className="flex items-center justify-center gap-3 mb-2">
                    <span className="text-6xl font-bold text-[#C67B5C]">$1,000</span>
                  </div>
                  <p className="text-lg text-[#1A1A1A]/70">Flat fee per placement</p>
                </div>
              </div>

              <div className="bg-[#C67B5C]/5 rounded-2xl p-6 mb-8">
                <p className="text-base text-[#1A1A1A]/80 text-center">
                  <strong className="text-[#C67B5C]">No upfront deposit required.</strong><br />
                  Full fee only due upon completion of placement.
                </p>
              </div>

              <ul className="space-y-4">
                {[
                  '90-Day Free Replacement Guarantee for Hiring',
                  'Save up to 70% compared to US talent',
                  'Vetted & Pre-qualified Candidates',
                  'Full candidate vetting process',
                  'Live interview with Mauricio',
                  '2–3 top candidate matches',
                  'Ongoing placement support'
                ].map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <i className="ri-check-line text-[#C67B5C] text-xl mt-0.5 flex-shrink-0"></i>
                    <span className="text-base text-[#1A1A1A]/80">{feature}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-10">
                <Link to="/#contact" className="block w-full bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-colors text-center whitespace-nowrap cursor-pointer">
                  Get Started Today
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-24 bg-[#F5F5F3]">
        <div className="max-w-3xl mx-auto px-8 text-center">
          <h2 className="text-4xl font-bold text-[#1A1A1A] mb-6">Want us to find and vet candidates for you?</h2>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="tel:+18329243888" 
              className="inline-flex items-center gap-3 bg-[#C67B5C] text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-[#B36A4D] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line text-xl"></i>
              Call (832) 924-3888
            </a>
            <a 
              href="mailto:mauricio@cadertalent.com" 
              className="inline-flex items-center gap-3 bg-white text-[#1A1A1A] px-12 py-5 rounded-full text-lg font-medium hover:bg-[#1A1A1A]/5 transition-colors border-2 border-[#1A1A1A]/10 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1A1A1A] text-white py-16">
        <div className="max-w-6xl mx-auto px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div className="lg:col-span-2">
              <Link to="/" className="flex items-center mb-4">
                <img src="https://static.readdy.ai/image/5287d4f4544f3b52e73e5c6adbaab8fc/8a8de03178bcb555df0aaf16906b9e92.png" alt="Cader Talent" className="h-40 w-auto" />
              </Link>
              <p className="text-white/60 text-base mb-8">Human judgment in modern hiring</p>
              <div className="space-y-3">
                <a href="tel:+18329243888" className="flex items-center gap-2 text-base text-white hover:text-[#C67B5C] transition-colors hover:underline cursor-pointer">
                  <i className="ri-phone-line"></i>
                  (832) 924-3888
                </a>
                <a href="mailto:mauricio@cadertalent.com" className="flex items-center gap-2 text-base text-white hover:text-[#C67B5C] transition-colors hover:underline cursor-pointer">
                  <i className="ri-mail-line"></i>
                  mauricio@cadertalent.com
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-sm text-white/60 font-semibold mb-4 uppercase tracking-wider">Quick Links</h4>
              <div className="space-y-3">
                <Link to="/#how-it-works" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">How It Works</Link>
                <Link to="/cost-calculator" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">Cost Calculator</Link>
                <Link to="/#about" className="block text-base text-white hover:text-[#C67B5C] transition-colors cursor-pointer">About</Link>
              </div>
            </div>
            <div>
              <h4 className="text-sm text-white/60 font-semibold mb-4 uppercase tracking-wider">Connect</h4>
              <a href="https://www.linkedin.com/company/cader-talent" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Company LinkedIn</a>
              <a href="https://www.linkedin.com/in/mauricio-cader-montealegre-88875b16/" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Mauricio's LinkedIn</a>
              <a href="https://www.facebook.com/profile.php?id=61586798219463" target="_blank" rel="noopener noreferrer" className="block text-base text-white hover:text-[#C67B5C] transition-colors mb-3 hover:underline cursor-pointer">Facebook Page</a>
              <p className="text-sm text-white/50">San Salvador, El Salvador</p>
            </div>
          </div>
          <div className="border-t border-white/15 pt-8 text-center">
            <p className="text-sm text-white/40">© 2025 Cader Talent. Built on trust. · <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#C67B5C] transition-colors cursor-pointer">Powered by Readdy</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
